import { z } from "zod";
import { ContactOutcomeSchema, EscalationDetailSchema } from "./contact-outcome.js";
import {
  AlarmStateSchema,
  ConcurrencyMetricSchema,
  EventTypeSchema,
  JourneyDimensionSchema,
  JourneyEdgeSchema,
  SeveritySchema,
} from "./event-envelope.js";
import {
  AlarmCaseIndexRecordSchema,
  EnvironmentRecordSchema,
  QuotaIncreaseRequestRecordSchema,
  QuotaIncreaseStatusSchema,
  ServiceQuotaSnapshotRecordSchema,
  SupportCaseRecordSchema,
} from "./records.js";

/**
 * Portal API contract (CLAUDE.md sections 11-12). Shared with the
 * frontend so the two cannot drift. Every list view that is scoped to a
 * customer resolves that customer from the JWT (role `customer`) or the
 * path (role `internal-team`); the response shapes are identical.
 */

export const RoleSchema = z.enum(["internal-team", "customer"]);
export type Role = z.infer<typeof RoleSchema>;

export const DayWindowSchema = z.union([z.literal(7), z.literal(30), z.literal(60), z.literal(90)]);
export type DayWindow = z.infer<typeof DayWindowSchema>;

/** How Gairm staff look at a customer's portal: as the customer would see it, or able to change it as the customer's manager would. */
export const ViewAsModeSchema = z.enum(["read", "write"]);
export type ViewAsMode = z.infer<typeof ViewAsModeSchema>;

/**
 * Requirements section 13.1. Non-sensitive, per user, stored on
 * `PortalUsers.preferences` and returned on `/v1/me`. Every timestamp
 * stays UTC in the tables; time zone and date format are presentation.
 */
export const ThemePreferenceSchema = z.enum(["light", "dark", "system"]);
/**
 * `us` / `intl` are numeric (`MM/DD/YYYY`, `DD/MM/YYYY`); `us-text` (`Oct 10, 2026`) and `intl-text`
 * (`10 Oct 2026`) name the month (#449). Values are only ever added, so every stored one stays valid.
 */
export const DateFormatPreferenceSchema = z.enum(["auto", "iso", "us", "intl", "us-text", "intl-text"]);
/**
 * The clock, its own choice (#449): `auto` is the hour cycle of the UI language taken in the device's region, never
 * derived from the date format; `12h` always reads AM/PM.
 */
export const TimeFormatPreferenceSchema = z.enum(["auto", "12h", "24h"]);
export const MotionPreferenceSchema = z.enum(["standard", "reduced", "auto"]);
/**
 * A nudge is a banner asking the user to finish something (enroll a passkey,
 * name a second manager). "later" hides it until the next sign-in - `at` is
 * compared with the token's auth_time; "never" hides it for good. Kept with
 * the preferences so it follows the user between devices.
 */
export const NudgeStateSchema = z.object({ state: z.enum(["later", "never"]), at: z.string() });
export type NudgeState = z.infer<typeof NudgeStateSchema>;
export const NudgeIdSchema = z.string().regex(/^[a-z][a-z0-9-]{0,31}$/);
export const AuditNoteStateSchema = z.enum(["shown", "minimized", "removed"]);
export type AuditNoteState = z.infer<typeof AuditNoteStateSchema>;
const preferenceFields = {
  theme: ThemePreferenceSchema,
  /** An IANA zone, or "auto" for whatever the device reports each session. */
  timeZone: z.string().min(1).max(64),
  dateFormat: DateFormatPreferenceSchema,
  timeFormat: TimeFormatPreferenceSchema,
  /** A portal path; the UI offers the role-appropriate list and falls back to "/" if it is no longer reachable. */
  landingPage: z.string().regex(/^\/[A-Za-z0-9\-/]*$/).max(128),
  motion: MotionPreferenceSchema,
  nudges: z.record(NudgeIdSchema, NudgeStateSchema),
  /**
   * Section 13.1: the environment each module opens on from the rail - Alarms on Production, Flow configuration on
   * Development. Keyed by module id; a module with no entry opens on the environment last looked at.
   */
  focusEnvironments: z.record(z.string().regex(/^[a-z][a-z0-9-]{0,31}$/), z.string().min(1).max(128)).refine((r) => Object.keys(r).length <= 32, "at most 32 modules"),
  /** The Audit page's "What this can and cannot show" note (#402): shown until the person minimizes or removes it. */
  auditNoteState: AuditNoteStateSchema,
};
export const UserPreferencesSchema = z.object({
  theme: preferenceFields.theme.default("system"),
  timeZone: preferenceFields.timeZone.default("auto"),
  dateFormat: preferenceFields.dateFormat.default("auto"),
  timeFormat: preferenceFields.timeFormat.default("auto"),
  landingPage: preferenceFields.landingPage.default("/"),
  motion: preferenceFields.motion.default("auto"),
  nudges: preferenceFields.nudges.default({}),
  focusEnvironments: preferenceFields.focusEnvironments.default({}),
  auditNoteState: preferenceFields.auditNoteState.default("shown"),
});
export type UserPreferences = z.infer<typeof UserPreferencesSchema>;
/** A patch: only the keys sent. Built without defaults on purpose - zod applies a default under partial() too, which would overwrite what is stored. */
export const UserPreferencesUpdateSchema = z.object(preferenceFields).partial();
export type UserPreferencesUpdate = z.infer<typeof UserPreferencesUpdateSchema>;

export const MeResponseSchema = z.object({
  subject: z.string(),
  email: z.string().optional(),
  role: RoleSchema,
  /** ADR-0009: the organization, not an environment. Per-environment roles are resolved per request. */
  organisationId: z.string().optional(),
  /** The API's version (root package.json); the UI shows it beside its own so a stale bundle is visible. */
  portalVersion: z.string().optional(),
  /** Set when a Gairm staff member is viewing this organization's portal (ADR-0011): the identity is still theirs. */
  viewAs: z
    .object({
      organisationId: z.string(),
      organisationName: z.string().optional(),
      mode: ViewAsModeSchema,
    })
    .optional(),
  /** The caller's own (section 13.1); defaults applied, so always complete when present. */
  preferences: UserPreferencesSchema.optional(),
  /** Section 13.2: what the header shows instead of the bare email, when set - `firstName lastName`, or the provider's name for a federated user. */
  displayName: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  /** ADR-0014: set for a federated user - the provider name they signed in through. Account hides password, MFA and passkeys for them; their name is the provider's. */
  identityProvider: z.string().optional(),
  /** #182: a change of email waiting for the new address to be confirmed; absent once confirmed, canceled or expired. */
  pendingEmail: z.object({ address: z.string(), expiresAt: z.string() }).optional(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

/** Section 13.3 [17]: the last 90 days of sign-ins from Cognito, and the user's own security-relevant audit rows. */
export interface SecurityActivityResponse {
  signIns: Array<{
    id: string;
    at: string;
    type: string;
    outcome: "pass" | "fail" | "in_progress" | "unknown";
    ip?: string;
    device?: string;
    place?: string;
    risk?: string;
  }>;
  changes: Array<{ id: string; at: string; action: string; result: string; actor: string; viaGairm: boolean }>;
  /** Cognito's advanced-security history is a Plus-plan feature; absent means this deployment cannot show it. */
  signInsAvailable: boolean;
}

export const SecurityReportSchema = z.object({ eventId: z.string().min(1).max(200), note: z.string().max(1000).optional() });

const NamePartSchema = z.string().trim().max(40);
/** Section 13.2: first and last name; the display name is derived from them. Both empty clears the name and the header falls back to the email. */
export const ProfileUpdateSchema = z.object({ firstName: NamePartSchema.default(""), lastName: NamePartSchema.default("") });
export type ProfileUpdate = z.infer<typeof ProfileUpdateSchema>;

/** #182: the address to move the account to. It becomes the sign-in only once its link is followed. */
export const EmailChangeRequestSchema = z.object({ email: z.string().trim().toLowerCase().max(254).email() });
/** #182: the token from the confirmation link's fragment. */
export const EmailChangeConfirmSchema = z.object({ token: z.string().min(16).max(200) });

/** POST /v1/me/email-change: what is now waiting for confirmation. */
export interface EmailChangeResponse {
  pendingEmail: { address: string; expiresAt: string };
}

/** POST /v1/public/email-revert (#580): a waiting change was stopped, or a made one undone and the account locked. */
export type EmailRevertResponse = { outcome: "canceled" } | { outcome: "reverted"; email: string };

/** POST /v1/me/email-change/confirm: the address now on the account. Every session has been signed out. */
export interface EmailChangeConfirmedResponse {
  email: string;
}

/**
 * The response envelopes below are what the portal API answers and the
 * web client reads - one type on both sides, so a body that drifts fails
 * `tsc` on whichever side changed (#284). Every route names one.
 */

/** A write that has nothing to say but that it happened. */
export interface OkResponse {
  ok: true;
}

/** PUT /v1/me/preferences: the stored map with defaults applied. */
export interface PreferencesResponse {
  preferences: UserPreferences;
}

/** PUT /v1/me/profile: the name as now held; a half that was cleared is absent. */
export interface UserProfileResponse {
  firstName?: string;
  lastName?: string;
  displayName?: string;
}

/** `First Last`, or whichever half exists; empty when neither does. */
export function displayNameFrom(firstName: string | undefined, lastName: string | undefined): string {
  return [firstName, lastName].map((s) => s?.trim() ?? "").filter(Boolean).join(" ");
}

/**
 * Req 30 (#67) — where the caller holds access, and as what.
 *
 * A role is held per environment, never across the organization
 * (ADR-0008/0009), and per-environment roles are resolved server-side
 * per request rather than carried as claims. So the UI cannot derive
 * this from the token; it has to ask.
 *
 * `scope` distinguishes the two shapes of answer. An `internal-team`
 * caller is not scoped by membership (their staff role answers, ADR-0017
 * section 4), so listing `Memberships` for them would be both wrong and
 * empty — the honest answer is "everything, by virtue of the group", and
 * `access` lists it per environment.
 */
export const MyMembershipSchema = z.object({
  environmentId: z.string(),
  friendlyName: z.string().optional(),
  awsAccountNumber: z.string().optional(),
  /** ADR-0017: the roles held here, directly or through a group; inside view-as, the built-in the mode gives. */
  roleIds: z.array(z.string()),
  /** Section 13.4: who granted it and when, from the membership row. */
  grantedBy: z.string().optional(),
  grantedAt: z.string().optional(),
});
export type MyMembership = z.infer<typeof MyMembershipSchema>;

export const MyMembershipsResponseSchema = z.object({
  scope: z.enum(["membership", "internal-team"]),
  organisationId: z.string().optional(),
  memberships: z.array(MyMembershipSchema),
  /** ADR-0017 section 4: a staff member's staff role; absent for a customer. */
  staffRole: z.enum(["gairm-staff", "gairm-reader", "gairm-superadmin"]).optional(),
  /**
   * ADR-0017 section 6: the caller's effective permissions per environment,
   * and which roles grant each - "you can edit hours on Production because
   * of Holidays editor". Staff see every permission on every environment
   * they are not excluded from (a reader: the read set).
   */
  access: z.array(
    z.object({
      environmentId: z.string(),
      friendlyName: z.string().optional(),
      permissions: z.array(z.string()),
      grantedBy: z.record(z.string(), z.array(z.string())),
      allProfiles: z.boolean(),
      profileIds: z.array(z.string()).optional(),
    }),
  ),
});
export type MyMembershipsResponse = z.infer<typeof MyMembershipsResponseSchema>;

/** One row of the Customers / Trusted Advisor list views (design.md "MVP views"). */
export const EnvironmentSummarySchema = z.object({
  environmentId: z.string(),
  friendlyName: z.string(),
  awsAccountNumber: z.string(),
  status: EnvironmentRecordSchema.shape.status,
  onboardingStatus: EnvironmentRecordSchema.shape.onboarding_status,
  connectivityVerifiedAt: z.string().optional(),
  lastHeartbeatAt: z.string().optional(),
  templateVersion: z.string().optional(),
  forwarderDlqDepth: z.number().int().optional(),
  /** False once the poller reported the support plan lacks the API (requirements 5) - the list says so instead of showing 0. */
  trustedAdvisorAvailable: z.boolean().optional(),
  supportApiAvailable: z.boolean().optional(),
  /** Count of events of the requested source in the requested window. */
  eventCount: z.number().int().nonnegative(),
  lastEventAt: z.string().optional(),
  openCriticalAlarms: z.number().int().nonnegative(),
  /** #352: how many flows a bound number reaches that skip the lander, and how many inbound flows log nothing. Absent until a template that lints has pushed. */
  flowLint: z.object({ unwiredFlows: z.number().int().nonnegative(), unloggedFlows: z.number().int().nonnegative() }).optional(),
});
export type EnvironmentSummary = z.infer<typeof EnvironmentSummarySchema>;

export const EnvironmentsListResponseSchema = z.object({
  window: DayWindowSchema,
  source: EventTypeSchema,
  currentTemplateVersion: z.string(),
  customers: z.array(EnvironmentSummarySchema),
});
export type EnvironmentsListResponse = z.infer<typeof EnvironmentsListResponseSchema>;

export const EnvironmentDetailResponseSchema = z.object({
  customer: EnvironmentRecordSchema.omit({ api_key_id: true, api_key_secret_arn: true, destination_config: true }),
  currentTemplateVersion: z.string(),
  enabledComponents: z.array(z.string()),
  /** False once the poller reported the support plan lacks Trusted Advisor (requirements.md section 5). */
  trustedAdvisorAvailable: z.boolean(),
});
export type EnvironmentDetailResponse = z.infer<typeof EnvironmentDetailResponseSchema>;

export const EventSummarySchema = z.object({
  eventId: z.string(),
  eventSource: EventTypeSchema,
  timestamp: z.string(),
  severity: SeveritySchema.optional(),
  /** Alarm name / TA check name - the one-line label for a list. */
  title: z.string(),
  state: AlarmStateSchema.optional(),
  resource: z.string().optional(),
  reason: z.string().optional(),
});
export type EventSummary = z.infer<typeof EventSummarySchema>;

export const EventsListResponseSchema = z.object({
  events: z.array(EventSummarySchema),
  nextCursor: z.string().optional(),
});
export type EventsListResponse = z.infer<typeof EventsListResponseSchema>;

export const AlarmsListResponseSchema = z.object({
  alarms: z.array(
    AlarmCaseIndexRecordSchema.omit({
      open_event_sk: true,
      last_event_sk: true,
      external_ticket_ref: true,
      external_ticket_display: true,
    }).extend({
      hasTicket: z.boolean(),
      /** #525: the number people use for the ticket (Connect's case reference number), where known. */
      ticketNumber: z.string().optional(),
      /** The backend's own id, shown only as a fallback's title when the number is not known. */
      ticketRef: z.string().optional(),
    }),
  ),
});
export type AlarmsListResponse = z.infer<typeof AlarmsListResponseSchema>;

export const QuotasListResponseSchema = z.object({
  quotas: z.array(ServiceQuotaSnapshotRecordSchema.omit({ sk: true, near_limit: true })),
  /** Whether this customer opted into QuotaIncreaseRequestRole (ADR-0002). */
  increaseRequestsAvailable: z.boolean(),
  /** #495: the organization's other environments in the same AWS account, by name - an account-level limit is shared with them. */
  sharedAccountWith: z.array(z.string()).default([]),
});
export type QuotasListResponse = z.infer<typeof QuotasListResponseSchema>;

export const SupportCasesListResponseSchema = z.object({
  cases: z.array(SupportCaseRecordSchema),
  /** False when the last poll reported the support plan does not include the API. */
  available: z.boolean(),
  /** ADR-0023: when the stack last finished a check with AWS Support; absent until a poll has recorded one. */
  checkedAt: z.string().optional(),
  /**
   * ADR-0023: whether the page may ask the stack to check now. When not, `reason` is the one sentence the
   * page shows in place of a Refresh button - a stack on a template that does not report the role, say.
   */
  refresh: z.object({ available: z.boolean(), reason: z.string().optional() }),
});
export type SupportCasesListResponse = z.infer<typeof SupportCasesListResponseSchema>;

/**
 * ADR-0023: the answer to "check AWS Support now". `started`: the stack's poller was invoked. `recent`:
 * one was asked for inside the throttle window, and that one is awaited instead. Either way the page
 * waits for the list's `checkedAt` to reach `requestedAt`.
 */
export const SupportCasesCheckResponseSchema = z.object({
  status: z.enum(["started", "recent"]),
  requestedAt: z.string(),
});
export type SupportCasesCheckResponse = z.infer<typeof SupportCasesCheckResponseSchema>;

export const ConcurrencyResponseSchema = z.object({
  hours: z.number().int().positive(),
  series: z.array(
    z.object({
      metric: ConcurrencyMetricSchema,
      points: z.array(z.object({ t: z.string(), v: z.number() })),
    }),
  ),
});
export type ConcurrencyResponse = z.infer<typeof ConcurrencyResponseSchema>;

/**
 * Focus's Yesterday row (the owner's decision of 2026-09-27): `production` - each organization's production
 * environment, the default - `all` for every environment the caller can see, or one `environment`. Sent as
 * `?scope=` (and `?environmentId=`); saved as the `focus` entry of `preferences.focusEnvironments`.
 */
export const FocusYesterdayScopeKindSchema = z.enum(["production", "all", "environment"]);
export type FocusYesterdayScopeKind = z.infer<typeof FocusYesterdayScopeKindSchema>;
export const FocusYesterdayScopeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("production") }),
  z.object({ kind: z.literal("all") }),
  z.object({ kind: z.literal("environment"), environmentId: z.string().min(1).max(128) }),
]);
export type FocusYesterdayScope = z.infer<typeof FocusYesterdayScopeSchema>;

/** The `focus` entry of `preferences.focusEnvironments` as a scope: `production` / `all`, or an environment id. Absent is production. */
export function focusYesterdayScopeOf(saved: string | undefined): FocusYesterdayScope {
  if (!saved || saved === "production") return { kind: "production" };
  if (saved === "all") return { kind: "all" };
  return { kind: "environment", environmentId: saved };
}

/** design.md "Focus page". `accountsGoneQuiet` is absent for the customer role (inherently cross-customer). */
export const FocusResponseSchema = z.object({
  tiles: z.object({
    criticalAlarmsOpen: z.number().int().nonnegative(),
    quotasNearLimit: z.number().int().nonnegative(),
    accountsGoneQuiet: z.number().int().nonnegative().optional(),
    openSupportCases: z.number().int().nonnegative(),
  }),
  criticalAlarms: z.array(
    z.object({
      environmentId: z.string(),
      friendlyName: z.string(),
      alarmName: z.string(),
      alarmArn: z.string(),
      openedAt: z.string(),
      lastSeenAt: z.string(),
      occurrenceCount: z.number().int(),
      hasTicket: z.boolean(),
    }),
  ),
  quotasNearLimit: z.array(
    z.object({
      environmentId: z.string(),
      friendlyName: z.string(),
      serviceCode: z.string(),
      quotaCode: z.string(),
      quotaName: z.string(),
      value: z.number(),
      usage: z.number(),
      usagePercent: z.number(),
      adjustable: z.boolean(),
      increaseRequestsAvailable: z.boolean(),
      /** Set when an increase request for this quota is already open with AWS - one per quota is all AWS allows. */
      openRequestStatus: QuotaIncreaseStatusSchema.optional(),
    }),
  ),
  accountsGoneQuiet: z
    .array(
      z.object({
        environmentId: z.string(),
        friendlyName: z.string(),
        lastHeartbeatAt: z.string().optional(),
        connectivityVerifiedAt: z.string().optional(),
        reason: z.enum(["never_connected", "no_heartbeat", "heartbeat_stale"]),
      }),
    )
    .optional(),
  /**
   * Section 8.9: a row from Journeys, so Focus says how the day went and not only what is on fire - yesterday (UTC)
   * over the scope asked for (`FocusYesterdayScope`; production by default, the owner's decision of 2026-09-27).
   * Absent only when the caller can see no environment with Journeys enabled.
   */
  yesterday: z
    .object({
      date: z.string(),
      /** What the row covers, so the page can name it. `environmentIds` is every environment in it, with data or not. */
      scope: z.object({
        kind: FocusYesterdayScopeKindSchema,
        environmentIds: z.array(z.string()),
        /** How many organizations those environments belong to - more than one only for Gairm staff. */
        organisations: z.number().int().nonnegative(),
      }),
      /** The picker's choices: every environment the caller can see with Journeys enabled, each organization's production marked. */
      choices: z.array(z.object({ environmentId: z.string(), friendlyName: z.string(), production: z.boolean() })),
      /** How many environments in the scope sent a contact record for the day. */
      environments: z.number().int().nonnegative(),
      contacts: z.number().int().nonnegative(),
      /** #462: the three outcomes - every contact is in one. */
      contained: z.number().int().nonnegative(),
      notContained: z.number().int().nonnegative(),
      escalated: z.number().int().nonnegative(),
      /** Escalated contacts that queued and never reached an agent. */
      abandoned: z.number().int().nonnegative(),
      poorAudio: z.number().int().nonnegative(),
      /** #448: contacts Connect's testing feature made, kept out of every number above and shown beside them. */
      testContacts: z.number().int().nonnegative().optional(),
      /** Changes made through the portal, from the audit day buckets (section 12). */
      configChanges: z.number().int().nonnegative(),
      /**
       * What `configChanges` counts. The audit day buckets are kept per organization and for everyone, never per
       * environment: `organisation` / `organisations` are the whole of the organizations the scope touches.
       */
      configChangesScope: z.enum(["organisation", "organisations", "all"]),
    })
    .optional(),
  /**
   * ADR-0008 / ADR-0017: every change request waiting for a decision into an environment where the caller holds
   * `configuration.promotions:approve`, on a profile they may see, newest first. Absent when they hold it nowhere,
   * so the section is not shown to someone who could never act on it.
   */
  pendingApprovals: z
    .array(
      z.object({
        promotionId: z.string(),
        kind: z.enum(["promotion", "direct_edit", "rollback", "binding"]),
        /** #481: on a binding request, what it would do to which alias. */
        binding: z.object({ op: z.enum(["bind", "unbind"]), alias: z.string() }).optional(),
        environmentId: z.string(),
        friendlyName: z.string(),
        sourceEnvironmentId: z.string(),
        /** The source environment's name when the caller can see it; a promotion from somewhere they cannot shows the id. */
        sourceFriendlyName: z.string().optional(),
        profileId: z.string(),
        profileName: z.string().optional(),
        requestedBy: z.string(),
        requestedAt: z.string(),
      }),
    )
    .optional(),
  generatedAt: z.string(),
});
export type FocusResponse = z.infer<typeof FocusResponseSchema>;

export const QuotaIncreaseRequestInputSchema = z.object({
  serviceCode: z.string().min(1),
  quotaCode: z.string().min(1),
  desiredValue: z.number().positive(),
});
export type QuotaIncreaseRequestInput = z.infer<typeof QuotaIncreaseRequestInputSchema>;

/**
 * Whose court a quota request's Support case is in. Read from the
 * SupportCases cache the customer's poller keeps - so only where their
 * plan includes the Support API; `pending-customer-action` is AWS asking
 * for more information, the thing the owner wanted to see at a glance.
 */
export const CaseCourtSchema = z.enum(["aws", "customer", "closed"]);
export type CaseCourt = z.infer<typeof CaseCourtSchema>;

export const QuotaIncreaseRequestViewSchema = QuotaIncreaseRequestRecordSchema.omit({ sk: true }).extend({
  caseStatus: z.string().optional(),
  caseCourt: CaseCourtSchema.optional(),
  caseUpdatedAt: z.string().optional(),
  /** AWS's last word on the case, where the cache holds one - the question they asked. */
  caseLastMessage: z.string().optional(),
});
export type QuotaIncreaseRequestView = z.infer<typeof QuotaIncreaseRequestViewSchema>;

export const QuotaIncreaseRequestsResponseSchema = z.object({
  requests: z.array(QuotaIncreaseRequestViewSchema),
  /** False where the environment's plan lacks the Support API: the case exists in Support Center, its status is not readable from here. */
  supportApiAvailable: z.boolean(),
});
export type QuotaIncreaseRequestsResponse = z.infer<typeof QuotaIncreaseRequestsResponseSchema>;

/**
 * What AWS said in the seconds after the request was filed: some quotas
 * are approved on the spot, others open a Support case that can take
 * days, and a few are still pending when we stop waiting.
 */
export const QuotaIncreaseRequestOutcomeSchema = z.object({
  ok: z.literal(true),
  awsRequestId: z.string().min(1),
  status: QuotaIncreaseStatusSchema,
  caseId: z.string().optional(),
  /** The limit AWS now reports, when the request was approved while we waited. */
  appliedValue: z.number().optional(),
});
export type QuotaIncreaseRequestOutcome = z.infer<typeof QuotaIncreaseRequestOutcomeSchema>;

// ---- Journeys (ADR-0003) ----

/** #448: real traffic, or the test traffic on its own. */
export const TrafficKindSchema = z.enum(["real", "test"]);
export type TrafficKind = z.infer<typeof TrafficKindSchema>;

export const JourneyPathsResponseSchema = z.object({
  windowStart: z.string(),
  windowEnd: z.string(),
  dimension: JourneyDimensionSchema,
  slice: z.string(),
  availableSlices: z.partialRecord(JourneyDimensionSchema, z.array(z.string())),
  contactCount: z.number().int().nonnegative(),
  edges: z.array(JourneyEdgeSchema),
  /**
   * #348: what central holds for this environment whatever the slice, so an
   * empty view can say which step is missing instead of "no data".
   */
  rollups: z.object({
    /** When the newest rollup arrived, in any window; absent when none ever has. */
    lastAt: z.string().optional(),
    /** Rollups stored for this window, every slice. */
    inWindow: z.number().int().nonnegative(),
    /** Contacts those rollups counted, from the all-contacts slice: zero with rollups present means the aggregator ran and read no flow log lines. */
    contactsInWindow: z.number().int().nonnegative(),
    /** #448: test contacts (Connect's testing feature) in this window's rollups - never in any other slice. */
    testContactsInWindow: z.number().int().nonnegative().optional(),
  }),
});
export type JourneyPathsResponse = z.infer<typeof JourneyPathsResponseSchema>;

export const ContactCandidateSchema = z.object({
  contactId: z.string(),
  initiationTimestamp: z.string(),
  disconnectTimestamp: z.string().optional(),
  channel: z.string().optional(),
  initiationMethod: z.string().optional(),
  queueName: z.string().optional(),
  customerEndpoint: z.string().optional(),
  systemEndpoint: z.string().optional(),
  agentConnected: z.boolean(),
});
export type ContactCandidate = z.infer<typeof ContactCandidateSchema>;

/** One day of the Journeys overview, as the dashboard draws it (requirements 8.4). */
export const JourneyDayTotalsSchema = z.object({
  contacts: z.number().int().nonnegative(),
  /** #462: the three outcomes - every contact is in one. */
  contained: z.number().int().nonnegative(),
  notContained: z.number().int().nonnegative(),
  escalated: z.number().int().nonnegative(),
  /** The detail beneath escalated. */
  answered: z.number().int().nonnegative(),
  transferredOut: z.number().int().nonnegative(),
  abandoned: z.number().int().nonnegative(),
  repeat7d: z.number().int().nonnegative(),
  answeredWaitSeconds: z.number().int().nonnegative(),
  abandonedWaitSeconds: z.number().int().nonnegative(),
  handleSeconds: z.number().int().nonnegative(),
  poorAudio: z.number().int().nonnegative(),
  byChannel: z.record(z.string(), z.number().int().nonnegative()),
});
export type JourneyDayTotals = z.infer<typeof JourneyDayTotalsSchema>;

export const JourneyOverviewResponseSchema = z.object({
  days: z.number().int().positive(),
  from: z.string(),
  to: z.string(),
  totals: JourneyDayTotalsSchema,
  /** The window before this one, same length, for the deltas. */
  prior: JourneyDayTotalsSchema,
  byDay: z.array(JourneyDayTotalsSchema.extend({ date: z.string() })),
  /** False until the environment's stack has sent contact records (EnableContactRecords). */
  available: z.boolean(),
  /** #448: which traffic the totals are - real by default, `test` when asked for on its own. */
  traffic: TrafficKindSchema.optional(),
  /** #448: test contacts in the window, whichever traffic the totals are - the labeled figure beside them. */
  testContacts: z.number().int().nonnegative().optional(),
});
export type JourneyOverviewResponse = z.infer<typeof JourneyOverviewResponseSchema>;

/** One contact as the Contacts list shows it: labels and timings, never content (ADR-0015). */
export const ContactRowSchema = z.object({
  contactId: z.string(),
  initiationTimestamp: z.string(),
  disconnectTimestamp: z.string().optional(),
  channel: z.string(),
  initiationMethod: z.string(),
  /** #462: contained, not contained or escalated. */
  outcome: ContactOutcomeSchema,
  /** How an escalated contact went on: answered, transferred out, abandoned in queue. Absent when the flow declared the escalation and nothing followed. */
  escalation: EscalationDetailSchema.optional(),
  disconnectReason: z.string().optional(),
  queueName: z.string().optional(),
  queueSeconds: z.number().int().nonnegative().optional(),
  handleSeconds: z.number().int().nonnegative().optional(),
  /** The number dialed - the customer's own. */
  dialled: z.string().optional(),
  poorAudio: z.boolean(),
  repeat: z.boolean().optional(),
  /** #448: Connect's testing feature made this contact; shown with a Test pill. */
  test: z.boolean().optional(),
});
export type ContactRow = z.infer<typeof ContactRowSchema>;

export const ContactRecordsResponseSchema = z.object({
  contacts: z.array(ContactRowSchema),
  from: z.string(),
  to: z.string(),
  /** Pass back as `cursor` for the page before. */
  cursor: z.string().optional(),
  available: z.boolean(),
  /** #348: when the newest contact this environment ever sent began, whatever the range and filters. Set only when this page is empty; absent then means none has arrived. */
  lastContactAt: z.string().optional(),
  /** #448: which contacts the list holds - real (the default), test, or all. */
  traffic: z.enum(["real", "test", "all"]).optional(),
  /** #448: test contacts on the days of this range, from the day counts; absent when they are not kept. */
  testContacts: z.number().int().nonnegative().optional(),
});
export type ContactRecordsResponse = z.infer<typeof ContactRecordsResponseSchema>;

export const ContactSearchResponseSchema = z.object({
  candidates: z.array(ContactCandidateSchema),
  truncated: z.boolean(),
});
export type ContactSearchResponse = z.infer<typeof ContactSearchResponseSchema>;

const PanelStatusSchema = z.enum(["ok", "not_available", "error"]);

/**
 * #348: why a panel is empty, as a step the portal can place in its
 * checklist - the note stays as the words. `not_declared`: the template
 * names no source for it; `not_found`: the named log group does not exist;
 * `access_denied`: the inspector role may not read it (a log group or
 * bucket outside what the template scoped it to); `timed_out`: the query
 * did not finish; `not_logged`: read, and nothing for this contact;
 * `no_agent` / `no_recording` / `analysis_missing`: the contact itself has
 * none; `not_permitted`: the person lacks the permission; `failed`:
 * anything else.
 */
export const PanelReasonSchema = z.enum(["not_declared", "not_found", "access_denied", "timed_out", "not_logged", "no_agent", "no_recording", "analysis_missing", "not_permitted", "failed"]);
export type PanelReason = z.infer<typeof PanelReasonSchema>;

export const FlowStepSchema = z.object({
  timestamp: z.string(),
  flowName: z.string().optional(),
  moduleType: z.string(),
  identifier: z.string().optional(),
  dwellMs: z.number().nonnegative().optional(),
  error: z.boolean(),
  parameters: z.record(z.string(), z.unknown()).optional(),
});

const AudioQualitySchema = z.object({ score: z.number().optional(), issues: z.array(z.string()) });

/**
 * One API call the assembler made in the customer's account, as their
 * CloudTrail records it (ADR-0015 section 9): `management` is always
 * logged, `data` only with a data-event selector, `presign` appears as a
 * GetObject under this session's identity when the browser plays it.
 */
export const JourneyReadSchema = z.object({
  at: z.string(),
  api: z.string(),
  resource: z.string(),
  event: z.enum(["management", "data", "presign"]),
});
export type JourneyRead = z.infer<typeof JourneyReadSchema>;

export const CallJourneySchema = z.object({
  contactId: z.string(),
  status: z.enum(["assembling", "complete", "failed"]),
  assembledAt: z.string().optional(),
  error: z.string().optional(),
  /** #348: why the whole assembly failed - no inspector role reported, the role refused us, or no Connect instance declared. */
  failureReason: z.enum(["no_role", "role_refused", "no_instance", "failed"]).optional(),
  /** Who asked for this assembly and when - the cache row in the customer's account says so. */
  requestedBy: z.string().optional(),
  requestedAt: z.string().optional(),
  /** Every read made to build this view, in order. What "verify it in your CloudTrail" is checked against. */
  reads: z.array(JourneyReadSchema).optional(),
  legs: z.array(
    z.object({
      contactId: z.string(),
      initiationTimestamp: z.string().optional(),
      disconnectTimestamp: z.string().optional(),
      initiationMethod: z.string().optional(),
      channel: z.string().optional(),
      queueName: z.string().optional(),
      agentId: z.string().optional(),
      customerEndpoint: z.string().optional(),
      systemEndpoint: z.string().optional(),
      previousContactId: z.string().optional(),
      disconnectReason: z.string().optional(),
      /** The rest of what DescribeContact says about the leg (requirements 8.5): the queue and agent spans, the handoff, the audio. */
      enqueuedAt: z.string().optional(),
      connectedToAgentAt: z.string().optional(),
      afterContactWorkMs: z.number().nonnegative().optional(),
      agentHoldMs: z.number().nonnegative().optional(),
      potentialDisconnectIssue: z.string().optional(),
      audioQuality: z.object({ agent: AudioQualitySchema.optional(), customer: AudioQualitySchema.optional() }).optional(),
      agentDevice: z.object({ platform: z.string().optional(), version: z.string().optional(), os: z.string().optional() }).optional(),
    }),
  ),
  timeline: z.object({ status: PanelStatusSchema, steps: z.array(FlowStepSchema), note: z.string().optional(), reason: PanelReasonSchema.optional() }),
  lambdas: z.object({
    status: PanelStatusSchema,
    invocations: z.array(
      z.object({
        functionName: z.string(),
        timestamp: z.string(),
        durationMs: z.number().optional(),
        logLines: z.array(z.string()),
      }),
    ),
    note: z.string().optional(),
    reason: PanelReasonSchema.optional(),
  }),
  bot: z.object({
    status: PanelStatusSchema,
    turns: z.array(z.object({ timestamp: z.string(), intent: z.string().optional(), utterance: z.string().optional(), slots: z.record(z.string(), z.string()).optional(), fallback: z.boolean() })),
    note: z.string().optional(),
    reason: PanelReasonSchema.optional(),
  }),
  agent: z.object({
    status: PanelStatusSchema,
    transcript: z.array(z.object({ participant: z.string(), beginOffsetMs: z.number(), content: z.string(), sentiment: z.string().optional() })),
    categories: z.array(z.string()),
    note: z.string().optional(),
    reason: PanelReasonSchema.optional(),
  }),
  dataDips: z.object({ status: PanelStatusSchema, items: z.array(z.object({ functionName: z.string(), request: z.unknown(), response: z.unknown() })), note: z.string().optional(), reason: PanelReasonSchema.optional() }),
  recording: z.object({ status: PanelStatusSchema, url: z.string().optional(), expiresAt: z.string().optional(), note: z.string().optional(), reason: PanelReasonSchema.optional() }),
});
export type CallJourney = z.infer<typeof CallJourneySchema>;
export type FlowStep = z.infer<typeof FlowStepSchema>;

/**
 * GET .../journeys/contacts/{contactId}: the assembled journey, or - on
 * the first look, while the assembler works in the customer's account -
 * the stub the cache row was claimed with, which has no panels yet. The
 * page polls until `status` leaves "assembling" and treats every panel as
 * optional until then.
 */
export type CallJourneyResponse = Pick<CallJourney, "contactId" | "status" | "assembledAt" | "error" | "failureReason" | "requestedBy" | "requestedAt" | "reads"> &
  Partial<Pick<CallJourney, "legs" | "timeline" | "lambdas" | "bot" | "agent" | "dataDips" | "recording">>;
